import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, apiFetch } from "@shared/lib/api";
import { useAuth } from "@shared/lib/auth";
import { Alert, Button, Card, Input, Spinner } from "@shared/components/ui";
import { Logo } from "@shared/components/Logo";
import type { ValidateInviteTokenResponse } from "@shared/lib/types";

const TOKEN_ERROR_MESSAGES: Record<string, string> = {
  invalid: "This link is invalid. Please use the Parent ID code from your email instead.",
  expired: "This link has expired. Please use the Parent ID code from your email instead.",
  already_used: "This link has already been used. Please use the Parent ID code from your email instead.",
  invite_revoked: "This invitation has been revoked. Please contact your childcare center for a new invite.",
};


export function SignupPage() {
  const auth = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Credentials state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [showAlreadyRegistered, setShowAlreadyRegistered] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Survey state (admin flow)
  const [step, setStep] = useState(1);
  const [centerName, setCenterName] = useState("");
  const [fullName, setFullName] = useState("");
  const [ownerDob, setOwnerDob] = useState("");

  // Parent flow state
  const [isParentFlow, setIsParentFlow] = useState(false);
  const [parentId, setParentId] = useState("");
  const [parentFullName, setParentFullName] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);

  // Magic link state
  const [tokenInfo, setTokenInfo] = useState<{ childNames: string[]; organizationName: string } | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [validatingToken, setValidatingToken] = useState(false);

  // Detect flow type from URL params
  useEffect(() => {
    const roleParam = searchParams.get("role");
    const inviteToken = searchParams.get("invite_token");

    if (roleParam === "parent" || inviteToken) {
      setIsParentFlow(true);
    }

    // Handle magic link token
    if (inviteToken) {
      setValidatingToken(true);
      apiFetch<ValidateInviteTokenResponse>(
        `/api/v1/parent_invites/validate_token?token=${encodeURIComponent(inviteToken)}`,
        { token: null }
      )
        .then(({ data }) => {
          if (data.valid) {
            setParentId(data.invite_code);
            setTokenInfo({
              childNames: data.child_names,
              organizationName: data.organization_name,
            });
          } else {
            setTokenError(TOKEN_ERROR_MESSAGES[data.reason] || TOKEN_ERROR_MESSAGES.invalid);
          }
        })
        .catch(() => {
          setTokenError(TOKEN_ERROR_MESSAGES.invalid);
        })
        .finally(() => {
          setValidatingToken(false);
        });
    }

    // Clear params from URL
    if (roleParam || inviteToken) {
      setSearchParams({}, { replace: true });
    }
  }, []);

  async function onAdminSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors([]);
    setShowAlreadyRegistered(false);
    setSubmitting(true);

    try {
      await auth.tenantSignUp({
        email,
        password,
        organizationName: centerName,
        fullName,
        ownerDob: ownerDob || undefined,
        tosAccepted,
      });
      nav("/welcome", { replace: true, state: { from: "/" } });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "";
      if (errorMessage.toLowerCase().includes("already registered")) {
        setShowAlreadyRegistered(true);
      } else {
        const errorMessages = err instanceof ApiError ? err.errors : [errorMessage || "Unknown error"];
        setErrors(errorMessages.length > 0 ? errorMessages : ["Something went wrong"]);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onParentSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors([]);
    setShowAlreadyRegistered(false);

    if (!parentId.trim()) {
      setErrors(["Parent ID code is required. Please contact your childcare center for your code."]);
      return;
    }

    setSubmitting(true);

    try {
      await auth.signUp({
        email,
        fullName: parentFullName,
        role: "parent",
        password,
        passwordConfirmation,
        parentInviteCode: parentId,
        tosAccepted,
      });
      nav("/welcome", { replace: true, state: { from: "/" } });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "";
      if (errorMessage.toLowerCase().includes("already registered")) {
        setShowAlreadyRegistered(true);
      } else {
        const errorMessages = err instanceof ApiError ? err.errors : [errorMessage || "Unknown error"];
        setErrors(errorMessages.length > 0 ? errorMessages : ["Something went wrong"]);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const alreadyRegisteredBanner = showAlreadyRegistered && (
    <div className="rounded-md border border-accent/20 bg-accent/10 p-3 text-sm">
      <p>An account with this email already exists.</p>
      <p className="mt-1">
        <Link className="font-medium text-foreground underline" to="/login">
          Sign in instead
        </Link>{" "}
        to continue setting up your account.
      </p>
    </div>
  );

  // ─── Parent Flow ───
  if (isParentFlow) {
    return (
      <div className="min-h-screen bg-bg">
        <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-12">
          <Logo size="lg" className="mb-8" />
          <Card title="Create account" className="w-full">
            {validatingToken ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Spinner />
                <p className="mt-4 text-sm text-muted">Validating your invitation link...</p>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={onParentSubmit}>
                <Alert items={errors} />
                {alreadyRegisteredBanner}
                {tokenError && (
                  <div className="rounded-md border border-border bg-warning-bg p-3 text-sm text-warning-text">
                    {tokenError}
                  </div>
                )}
                {tokenInfo && (
                  <div className="rounded-md border border-accent/20 bg-accent/5 p-3 text-sm">
                    <p className="font-medium text-accent">You're signing up to connect with:</p>
                    <p className="mt-1 text-foreground">{tokenInfo.childNames.join(", ")}</p>
                    <p className="mt-1 text-xs text-muted">at {tokenInfo.organizationName}</p>
                  </div>
                )}
                <Input label="Full name" value={parentFullName} onChange={(e) => setParentFullName(e.target.value)} />
                <Input
                  label="Parent ID"
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                  hint={tokenInfo ? "Pre-filled from your invitation link." : "Enter the Parent ID code provided by your childcare center."}
                  autoComplete="one-time-code"
                  required
                  readOnly={!!tokenInfo}
                  className={tokenInfo ? "bg-muted/10" : ""}
                />
                <Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                <Input label="Password confirmation" type="password" value={passwordConfirmation} onChange={(e) => setPasswordConfirmation(e.target.value)} autoComplete="new-password" />
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={tosAccepted}
                    onChange={(e) => setTosAccepted(e.target.checked)}
                    className="mt-1 rounded border-border"
                    required
                  />
                  <span className="text-muted">
                    I agree to the{" "}
                    <Link to="/terms" target="_blank" className="text-foreground underline">
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy" target="_blank" className="text-foreground underline">
                      Privacy Policy
                    </Link>
                  </span>
                </label>
                <div className="flex items-center justify-between gap-3">
                  <Button type="submit" disabled={submitting || !tosAccepted}>{submitting ? "Creating..." : "Create account"}</Button>
                  {submitting ? <Spinner /> : null}
                </div>
              </form>
            )}
            <div className="mt-4 text-sm text-muted">
              Already have an account?{" "}
              <Link className="font-medium text-foreground underline" to="/login">Sign in</Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // ─── Admin Flow (Multi-Step Survey) ───
  return (
    <div className="min-h-screen bg-bg">
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-12">
        <Logo size="lg" className="mb-8" />
        <Card title="Create account" className="w-full">
          {/* Step indicator */}
          <div
            className="mb-5 flex items-center justify-center gap-2 text-sm text-muted"
            aria-live="polite"
            aria-label={`Step ${step} of 2`}
          >
            <span className={step === 1 ? "font-semibold text-foreground" : ""}>Step 1</span>
            <span className="text-border">&mdash;</span>
            <span className={step === 2 ? "font-semibold text-foreground" : ""}>Step 2</span>
          </div>

          {/* Step 1: Survey */}
          {step === 1 && (
            <div className="space-y-5">
              <Input
                label="Center / program name"
                value={centerName}
                onChange={(e) => setCenterName(e.target.value)}
                placeholder="e.g. Sunshine Learning Center"
              />
              <Input
                label="Your full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
              <Input
                label="Date of birth"
                type="date"
                value={ownerDob}
                onChange={(e) => setOwnerDob(e.target.value)}
                hint="Used for identity verification to comply with state licensing requirements."
              />
              <Button
                type="button"
                disabled={!centerName.trim() || !fullName.trim() || !ownerDob}
                onClick={() => setStep(2)}
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step 2: Credentials */}
          {step === 2 && (
            <form className="space-y-4" onSubmit={onAdminSubmit}>
              <Alert items={errors} />
              {alreadyRegisteredBanner}
              <Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
              <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
              <Input label="Password confirmation" type="password" value={passwordConfirmation} onChange={(e) => setPasswordConfirmation(e.target.value)} autoComplete="new-password" />
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  className="mt-1 rounded border-border"
                  required
                />
                <span className="text-muted">
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" className="text-foreground underline">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" target="_blank" className="text-foreground underline">
                    Privacy Policy
                  </Link>
                </span>
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-sm font-medium text-muted transition-colors hover:text-foreground"
                >
                  &larr; Back
                </button>
                <Button type="submit" disabled={submitting || !tosAccepted}>
                  {submitting ? "Creating..." : "Create account"}
                </Button>
                {submitting ? <Spinner /> : null}
              </div>
            </form>
          )}

          <div className="mt-4 text-sm text-muted">
            Already have an account?{" "}
            <Link className="font-medium text-foreground underline" to="/login">Sign in</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
